
import React, { useEffect, useState } from 'react';
import { CloudService } from '@/services/CloudService';
import { Database, RefreshCw, AlertTriangle, AlertCircle, Music, Volume2, Save, Disc, FileAudio, Video } from 'lucide-react';
import { motion } from 'motion/react';

interface StorageUsage {
  melodies: number;
  sounds: number;
  saves: number;
  songs: number;
  converted: number;
  videos: number;
  total: number;
  limit: number;
  plan: string;
}

export const CloudStorageSection: React.FC = () => {
  const [usage, setUsage] = useState<StorageUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const DEFAULT_LIMIT = 5 * 1024 * 1024; // 5MB fallback

  const fetchUsage = async () => {
    setIsLoading(true);
    const data = await CloudService.getStorageUsage();
    if (data) setUsage(data);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchUsage();
  }, []);

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const storageLimit = usage?.limit || DEFAULT_LIMIT;

  const calculatePercent = (bytes: number) => {
    return Math.min(100, (bytes / storageLimit) * 100);
  };

  const usageBreakdown = [
    { label: 'MIDI', value: usage?.melodies || 0, color: 'bg-cyan-600', icon: <Music className="w-3 h-3" /> },
    { label: 'Audio', value: usage?.sounds || 0, color: 'bg-cyan-500', icon: <Volume2 className="w-3 h-3" /> },
    { label: 'Saves', value: usage?.saves || 0, color: 'bg-cyan-400', icon: <Save className="w-3 h-3" /> },
    { label: 'Videos', value: usage?.videos || 0, color: 'bg-cyan-300', icon: <Video className="w-3 h-3" /> },
  ];

  const totalUsage = usage?.total || 0;
  const percentUsed = calculatePercent(totalUsage);
  const isNearLimit = percentUsed > 80;
  const isOverLimit = percentUsed >= 100;

  return (
    <div className="w-full space-y-4">
      {/* Mini Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-cyan-600" />
          <h3 className="text-[10px] font-black uppercase tracking-widest text-zinc-900">Cloud Storage</h3>
        </div>
        <button 
          onClick={fetchUsage} 
          disabled={isLoading}
          className="p-1.5 hover:bg-cyan-500/5 rounded-lg text-cyan-600 transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Main Usage Bar Card */}
      <div className="bg-zinc-50 border border-cyan-500/5 rounded-2xl p-5">
        <div className="flex justify-between items-end mb-4 overflow-hidden">
          <div className="min-w-0">
            <span className="text-[8px] font-bold uppercase tracking-tighter text-zinc-400 truncate block">Stored Data</span>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-2xl font-black text-zinc-900 leading-none">{formatSize(totalUsage)}</span>
              <span className="text-[9px] font-mono text-cyan-600/30">/ {formatSize(storageLimit)}</span>
            </div>
          </div>
          <div className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase whitespace-nowrap shrink-0 shadow-lg shadow-cyan-500/10 ${
            isOverLimit ? 'bg-red-500 text-white shadow-red-500/20' : 
            (isNearLimit ? 'bg-yellow-400 text-black shadow-yellow-500/20' : 'bg-cyan-600 text-white shadow-cyan-500/20')
          }`}>
            {percentUsed.toFixed(0)}% Full
          </div>
        </div>

        <div className="h-2 w-full bg-zinc-200 rounded-full overflow-hidden flex shadow-inner mb-2">
          {usageBreakdown.map((item) => (
            <motion.div
              key={item.label}
              initial={{ width: 0 }}
              animate={{ width: `${(item.value / storageLimit) * 100}%` }}
              className={`${item.color} h-full border-r border-white/10 last:border-0`}
            />
          ))}
        </div>

        {isOverLimit && (
          <div className="flex items-center gap-2 mt-3 text-red-500">
            <AlertCircle className="w-3 h-3" />
            <span className="text-[8px] font-black uppercase">Quota Exceeded. Please delete files.</span>
          </div>
        )}
      </div>

      {/* Grid Breakdown */}
      <div className="grid grid-cols-2 gap-2">
        {usageBreakdown.map((item) => (
          <div key={item.label} className="p-2 sm:p-3 bg-white border border-cyan-500/5 rounded-xl flex items-center gap-2 transition-all hover:border-cyan-500/20 overflow-hidden shadow-sm shadow-cyan-500/[0.02]">
            <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center ${item.color} text-white shrink-0 shadow-md`}>
              {item.icon}
            </div>
            <div className="flex flex-col min-w-0 overflow-hidden">
              <span className="text-[7px] sm:text-[8px] font-black uppercase tracking-tight text-cyan-600/50 leading-tight">{item.label}</span>
              <span className="text-[9px] sm:text-[10px] font-bold text-zinc-900 leading-tight break-all">{formatSize(item.value)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
